import { INVOICING_STATUS } from 'src/app/core/enums/invoicing-status.enum';
import { TICKET_TYPE } from 'src/app/core/enums/ticket-type.enum';
import { GENERAL_PERMISSIONS } from 'src/app/features/permission/domain/enums/general-permissions.enum';
import { PAYMENT_PERMISSION } from 'src/app/features/permission/domain/enums/payment-permissions.enum';
import { PAYMENT_METHOD } from 'src/app/shared/enums/payment-method.enum';
import { IApiTicket } from '../interfaces/ticket-api.interface';
import {
  entryApiMock1,
  entryApiMock2,
  entryApiMock3,
  entryApiMock4,
  entryApiMock5,
  entryApiMock6
} from '../../../entry/infrastructure/mocks/entry.mock';

/**
 * Mock for a standard payment ticket
 * Represents a typical customer transaction with card payment
 */
export const ticketApiMock1: IApiTicket = {
  _id: '550e8400-e29b-41d4-a716-446655440001',
  negocio_id: '6507f1f45e88bd1a92b30001',
  service_id: '6507f1f45e88bd1a92b30002',
  account_id: '6507f1f45e88bd1a92b30003',
  serie: 'POS-2024-001',
  organization_name: 'Restaurante La Marina',
  address: 'Calle del Puerto 15, 08003 Barcelona',
  cif: 'B12345678',
  paid_at: 1703847600000, // 2023-12-29 12:00:00
  total: 60.90,
  entries: [entryApiMock1, entryApiMock2, entryApiMock4],
  tax_summary: [
    {
      tax_base: 50.33,
      tax: 21,
      tax_amount: 10.57,
      gross_amount: 60.90
    }
  ],
  products_quantity: 4,
  currency: 'EUR',
  operations: [
    {
      operation_id: 'op_550e8400e29b41d4a716446655440001',
      payment_method: PAYMENT_METHOD.card,
      amount: 60.90,
      change: 0,
      method: {
        type: 'card',
        name: 'Visa',
        alias: 'Visa ****1234',
        last4: '1234',
        expires: '12/28'
      }
    }
  ],
  waiter: {
    _id: '6507f1f45e88bd1a92b30004',
    name: 'María González',
    organization_id: '6507f1f45e88bd1a92b30001',
    image: '',
    password: undefined,
    activated: true,
    archived: false,
    is_working: true,
    device_ids: ['device_001'],
    negocio_id: '6507f1f45e88bd1a92b30001',
    permission_group_ids: [GENERAL_PERMISSIONS.canCreateAccount, PAYMENT_PERMISSION.canPayWithCash],
    created_by: 'admin',
    created_at: 1703760000000,
    updated_by: 'admin',
    updated_at: 1703760000000,
    removed_at: 0,
    removed_by: ''
  },
  location: {
    _id: '6507f1f45e88bd1a92b30005',
    negocio_id: '6507f1f45e88bd1a92b30001',
    is_general: false,
    cash_drawers_ids: ['drawer_001'],
    archived: false,
    activated: true,
    name: 'Terraza Principal',
    color: '#2196F3'
  },
  type: TICKET_TYPE.simple,
  s3_url: 'https://s3.amazonaws.com/tickets/550e8400-e29b-41d4-a716-446655440001.pdf',
  client_id: 'client_001',
  service_date: 1703760000000,
  status: INVOICING_STATUS.issued,
  created_at: 1703847600000,
  updated_at: 1703847600000,
  removed_at: 0,
  invoice_module_extensions: []
};

/**
 * Mock for a refund ticket
 * Represents a refund transaction with negative amounts
 */
export const ticketApiMock2: IApiTicket = {
  _id: '550e8400-e29b-41d4-a716-446655440002',
  associated_ticket_id: '550e8400-e29b-41d4-a716-446655440001', // References the original ticket
  negocio_id: '6507f1f45e88bd1a92b30001',
  service_id: '6507f1f45e88bd1a92b30002',
  account_id: '6507f1f45e88bd1a92b30003',
  serie: 'REF-2024-001',
  organization_name: 'Restaurante La Marina',
  address: 'Calle del Puerto 15, 08003 Barcelona',
  cif: 'B12345678',
  paid_at: 1703851200000, // 2023-12-29 13:00:00
  total: -15.80,
  entries: [entryApiMock3, entryApiMock5], // Entry with negative quantity and discounted entry
  tax_summary: [
    {
      tax_base: -13.06,
      tax: 21,
      tax_amount: -2.74,
      gross_amount: -15.80
    }
  ],
  products_quantity: 2,
  currency: 'EUR',
  operations: [
    {
      operation_id: 'op_550e8400e29b41d4a716446655440002',
      payment_method: PAYMENT_METHOD.cash,
      amount: -15.80,
      change: 0
    }
  ],
  waiter: {
    _id: '6507f1f45e88bd1a92b30006',
    name: 'Carlos Rodríguez',
    organization_id: '6507f1f45e88bd1a92b30001',
    image: '',
    password: undefined,
    activated: true,
    archived: false,
    is_working: true,
    device_ids: ['device_002'],
    negocio_id: '6507f1f45e88bd1a92b30001',
    permission_group_ids: [GENERAL_PERMISSIONS.canCreateAccount, PAYMENT_PERMISSION.canPayWithCard, GENERAL_PERMISSIONS.canOpenCashDrawer],
    created_by: 'admin',
    created_at: 1703760000000,
    updated_by: 'admin',
    updated_at: 1703760000000,
    removed_at: 0,
    removed_by: ''
  },
  location: {
    _id: '6507f1f45e88bd1a92b30005',
    negocio_id: '6507f1f45e88bd1a92b30001',
    is_general: false,
    cash_drawers_ids: ['drawer_001'],
    archived: false,
    activated: true,
    name: 'Terraza Principal',
    color: '#2196F3'
  },
  type: TICKET_TYPE.refund,
  s3_url: 'https://s3.amazonaws.com/tickets/550e8400-e29b-41d4-a716-446655440002.pdf',
  client_id: 'client_001',
  service_date: 1703760000000,
  status: INVOICING_STATUS.issued,
  created_at: 1703851200000,
  updated_at: 1703851200000,
  removed_at: 0,
  invoice_module_extensions: []
};

/**
 * Mock for a booking ticket
 * Represents a reservation transaction with booking payment method
 */
export const ticketApiMock3: IApiTicket = {
  _id: '550e8400-e29b-41d4-a716-446655440003',
  negocio_id: '6507f1f45e88bd1a92b30001',
  service_id: '6507f1f45e88bd1a92b30002',
  account_id: '6507f1f45e88bd1a92b30007',
  serie: 'BKG-2024-001',
  organization_name: 'Restaurante La Marina',
  address: 'Calle del Puerto 15, 08003 Barcelona',
  cif: 'B12345678',
  paid_at: 1703934000000, // 2023-12-30 12:00:00
  total: 145.00,
  entries: [entryApiMock1, entryApiMock2, entryApiMock6],
  tax_summary: [
    {
      tax_base: 119.83,
      tax: 21,
      tax_amount: 25.17,
      gross_amount: 145.00
    }
  ],
  products_quantity: 4,
  currency: 'EUR',
  operations: [
    {
      operation_id: 'op_550e8400e29b41d4a716446655440003',
      payment_method: PAYMENT_METHOD.booking,
      amount: 145.00,
      change: 0
    }
  ],
  waiter: {
    _id: '6507f1f45e88bd1a92b30004',
    name: 'María González',
    organization_id: '6507f1f45e88bd1a92b30001',
    image: '',
    password: undefined,
    activated: true,
    archived: false,
    is_working: true,
    device_ids: ['device_001'],
    negocio_id: '6507f1f45e88bd1a92b30001',
    permission_group_ids: [GENERAL_PERMISSIONS.canCreateAccount, PAYMENT_PERMISSION.canPayWithCash],
    created_by: 'admin',
    created_at: 1703760000000,
    updated_by: 'admin',
    updated_at: 1703760000000,
    removed_at: 0,
    removed_by: ''
  },
  location: {
    _id: '6507f1f45e88bd1a92b30008',
    negocio_id: '6507f1f45e88bd1a92b30001',
    is_general: false,
    cash_drawers_ids: ['drawer_002'],
    archived: false,
    activated: true,
    name: 'Salón VIP',
    color: '#FF9800'
  },
  type: TICKET_TYPE.simple,
  s3_url: 'https://s3.amazonaws.com/tickets/550e8400-e29b-41d4-a716-446655440003.pdf',
  client_id: 'client_booking_001',
  service_date: 1703934000000,
  status: INVOICING_STATUS.pending,
  created_at: 1703934000000,
  updated_at: 1703934000000,
  removed_at: 0,
  invoice_module_extensions: []
};

/**
 * Array containing all ticket API mocks for easy access
 */
export const ticketApiMocks: IApiTicket[] = [
  ticketApiMock1,
  ticketApiMock2,
  ticketApiMock3
];

/**
 * Helper function to get a ticket API mock by ID
 * @param id - The ticket ID to search for
 * @returns The ticket API mock or undefined if not found
 */
export function getTicketApiMockById(id: string): IApiTicket | undefined {
  return ticketApiMocks.find(ticket => ticket._id === id);
}

/**
 * Helper function to get ticket API mocks by type
 * @param type - The ticket type to filter by
 * @returns Array of ticket API mocks matching the type
 */
export function getTicketApiMocksByType(type: string): IApiTicket[] {
  return ticketApiMocks.filter(ticket => ticket.type === type);
}

/**
 * Helper function to get refund tickets
 * @returns Array of refund ticket API mocks
 */
export function getRefundTicketApiMocks(): IApiTicket[] {
  return ticketApiMocks.filter(ticket => ticket.type === TICKET_TYPE.refund);
}
